<script lang="ts" setup>
import { computed } from 'vue'

import { masterAttributes } from '@collector/shared-ui-domain'
import { opChain } from '@collector/shared-utils'
import {
  IncidentActionType,
  IncidentId,
  Property,
} from '@collector/sportsapi-client-legacy'
import { useSportOptions } from '@mobile/views/Relation/DetailsPanel/Sports/composables/useSportOptions'
import { useEventRelationSettings } from '@mobile/views/Relation/DetailsPanel/Sports/useEventRelationSettings'
import { getCurrentXG } from '@mobile/views/Relation/DetailsPanel/utils'
import IncidentResult from '@mobile/views/Relation/Shared/IncidentResult/IncidentResult.vue'
import { ProbableLinkedIncident } from '@mobile/views/Relation/Shared/RelationDependencies/types'
import { useEventInjections } from '@mobile/views/Relation/Shared/RelationDependencies/useEventInjections'
import RelationIcon, {
  RelationIconType,
} from '@mobile/views/Relation/Shared/RelationIcon/RelationIcon.vue'

import { Result } from '../../Shared/IncidentResult'

type Props = {
  linkedIncident: ProbableLinkedIncident
  result: Result | null
  participantsIncidents?: IncidentId[]
  detailsIncidents?: IncidentId[]
  showParticipants?: boolean
  showAttributes?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  participantsIncidents: () => [],
  detailsIncidents: () => [],
  showParticipants: false,
  showAttributes: true,
})
const eventInjections = useEventInjections()
const { sport } = eventInjections
const sportOptionsComposable = useSportOptions(sport)
const { attributesConfiguration } = sportOptionsComposable
const { participantsIncidents: pIncidents, detailsIncidents: dIncidents } =
  useEventRelationSettings(eventInjections, sportOptionsComposable)

function getPropertiesValue(
  properties: Property[],
  filter?: (property: Property) => boolean,
): string {
  return properties
    .filter(filter ?? (() => true))
    .map((property) => property.value)
    .join(', ')
}

function getAttributesNames(attributeIds: number[]): string {
  const attributesToShow = [
    ...(attributesConfiguration[props.linkedIncident.incident.incident_id] ||
      []),
    ...masterAttributes,
  ]

  const filteredAttributes =
    props.linkedIncident.sportIncident.attributes.filter(
      (attribute) =>
        attributeIds.includes(attribute.id) &&
        attributesToShow.includes(attribute.id),
    )

  return filteredAttributes.map((attribute) => attribute.name).join(', ')
}

const boldIncidentName = computed(
  () =>
    pIncidents.value.includes(props.linkedIncident.incident.incident_id) ||
    dIncidents.value.includes(props.linkedIncident.incident.incident_id),
)

const isDeleted = computed(
  () => props.linkedIncident.incident.action === IncidentActionType.DELETE,
)

const currentXG = computed(() =>
  getCurrentXG(props.linkedIncident.incident.properties),
)
</script>

<template>
  <div class="flex items-center">
    <RelationIcon
      class="mr-2"
      :size="18"
      :iconId="linkedIncident.incident.incident_id"
      :type="RelationIconType.Incidents"
    />
    <div class="flex flex-col">
      <span
        v-if="linkedIncident.incident.participant_name"
        class="text-xxs font-bold"
        :class="
          opChain(
            linkedIncident.probableLinkedParticipant,
            (v) => v.css.textClass,
          )
        "
      >
        {{ linkedIncident.incident.participant_name }}
      </span>
      <div
        class="text-base"
        :class="{ 'font-bold': boldIncidentName }"
      >
        <span>{{ linkedIncident.incident.incident_name }}</span>
        <IncidentResult
          :linkedIncident
          :result
          :isDeleted
        />

        <span
          v-if="
            getAttributesNames(linkedIncident.incident.attribute_ids).length &&
            showAttributes
          "
          class="font-normal"
        >
          ({{ getAttributesNames(linkedIncident.incident.attribute_ids) }})
          <span
            v-if="
              linkedIncident.incident.properties &&
              getPropertiesValue(
                linkedIncident.incident.properties,
                (prop) => prop.short_name !== 'xG',
              ).length
            "
          >
            ,
          </span>
        </span>
        <span
          v-if="
            linkedIncident.incident.properties &&
            opChain(linkedIncident.incident.properties, (v) => v.length)
          "
          class="pl-1 font-normal"
        >
          {{
            getPropertiesValue(
              linkedIncident.incident.properties,
              (prop) => prop.short_name !== 'xG',
            )
          }}
        </span>
      </div>
      <div class="text-xxs font-bold">
        <span v-if="linkedIncident.incident.subparticipant_name">
          {{ linkedIncident.incident.subparticipant_name }}
        </span>
        <span
          v-if="
            opChain(
              linkedIncident.incident.additional_info,
              (v) => v.assistant_id,
            )
          "
          class="ml-1"
        >
          ({{
            opChain(
              linkedIncident.incident.additional_info,
              (v) => v.assistant_name,
            )
          }})
        </span>
        <span
          v-if="currentXG !== undefined"
          class="ml-1"
        >
          (xG = {{ currentXG }})
        </span>
      </div>
    </div>
  </div>
</template>
